import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, MapPin, Link2, Loader2, CreditCard, AlertCircle, ImagePlus, X, ArrowLeft, Home } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface UserCredits {
  total_credits: number;
  used_credits: number;
}

const Submit = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";
  
  const [user, setUser] = useState<any>(null);
  const [userCredits, setUserCredits] = useState<UserCredits | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("event");
  const [categories, setCategories] = useState<any[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form states
  const [formData, setFormData] = useState({
    title: "",
    title_ar: "",
    description: "",
    description_ar: "",
    date: "",
    start_time: "",
    end_time: "",
    location: "",
    location_ar: "",
    external_link: "",
    category: "",
    x_space_url: "",
    scheduled_at: "",
    social_type: "",
    social_link: "",
    members_count: "",
  });

  useEffect(() => {
    checkAuth();
    fetchCategories();
  }, []);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/auth");
      return;
    }
    setUser(user);
    fetchUserCredits(user.id);
    setLoading(false);
  };

  const fetchUserCredits = async (userId: string) => {
    const { data } = await supabase
      .from("user_credits")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();

    setUserCredits(data);
  };

  const fetchCategories = async () => {
    const { data } = await supabase
      .from("categories")
      .select("*")
      .eq("is_visible", true)
      .order("display_order");

    if (data) setCategories(data);
  };

  const remainingCredits = userCredits ? userCredits.total_credits - userCredits.used_credits : 0;

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(isRTL ? "يرجى اختيار صورة صالحة" : "Please select a valid image");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(isRTL ? "حجم الصورة كبير جداً (الحد الأقصى 5MB)" : "Image too large (max 5MB)");
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile || !user) return null;

    setUploadingImage(true);
    try {
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from('submission-images')
        .upload(fileName, imageFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('submission-images')
        .getPublicUrl(data.path);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error(isRTL ? "فشل رفع الصورة" : "Image upload failed");
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async () => {
    if (remainingCredits <= 0) {
      toast.error(isRTL ? "لا يوجد رصيد كافٍ" : "Insufficient credits");
      return;
    }

    if (!formData.title) {
      toast.error(isRTL ? "العنوان مطلوب" : "Title is required");
      return;
    }

    setSubmitting(true);

    try {
      // Upload image first if exists
      let imageUrl: string | null = null;
      if (imageFile) {
        imageUrl = await uploadImage();
      }

      const submissionData: any = {
        user_id: user.id,
        submission_type: activeTab,
        title: formData.title,
        title_ar: formData.title_ar || null,
        description: formData.description || null,
        description_ar: formData.description_ar || null,
        image_url: imageUrl,
      };

      if (activeTab === "event") {
        submissionData.date = formData.date || null;
        submissionData.start_time = formData.start_time || null;
        submissionData.end_time = formData.end_time || null;
        submissionData.location = formData.location || null;
        submissionData.location_ar = formData.location_ar || null;
        submissionData.external_link = formData.external_link || null;
        submissionData.category = formData.category || null;
      } else if (activeTab === "x_space") {
        submissionData.x_space_url = formData.x_space_url || null;
        submissionData.scheduled_at = formData.scheduled_at ? new Date(formData.scheduled_at).toISOString() : null;
      } else if (activeTab === "social_account") {
        submissionData.social_type = formData.social_type || null;
        submissionData.social_link = formData.social_link || null;
        submissionData.members_count = formData.members_count ? parseInt(formData.members_count) : null;
      }

      // Insert submission
      const { error: submissionError } = await supabase
        .from("user_submissions")
        .insert(submissionData);

      if (submissionError) throw submissionError;

      // Update credits
      const { error: creditsError } = await supabase
        .from("user_credits")
        .update({
          used_credits: (userCredits?.used_credits || 0) + 1,
          updated_at: new Date().toISOString(),
        })
        .eq("user_id", user.id);

      if (creditsError) throw creditsError;

      toast.success(isRTL ? "تم إرسال طلبك بنجاح!" : "Your submission was sent successfully!");
      
      // Reset form and refresh credits
      setFormData({
        title: "", title_ar: "", description: "", description_ar: "",
        date: "", start_time: "", end_time: "", location: "", location_ar: "",
        external_link: "", category: "", x_space_url: "", scheduled_at: "",
        social_type: "", social_link: "", members_count: "",
      });
      removeImage();
      fetchUserCredits(user.id);

    } catch (error: any) {
      console.error("Submission error:", error);
      toast.error(isRTL ? "حدث خطأ في الإرسال" : "Submission error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 pt-24 max-w-3xl">
        {/* Back to Home Button */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/")}
            className="gap-2 text-muted-foreground hover:text-foreground"
          >
            {isRTL ? <ArrowLeft className="h-4 w-4 rotate-180" /> : <ArrowLeft className="h-4 w-4" />}
            <Home className="h-4 w-4" />
            <span>{isRTL ? "الصفحة الرئيسية" : "Home"}</span>
          </Button>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">
            {isRTL ? "إضافة محتوى جديد" : "Add New Content"}
          </h1>
          <p className="text-muted-foreground">
            {isRTL ? "أضف فعاليتك أو مساحتك أو حسابك" : "Add your event, space, or account"}
          </p>
        </div>

        {/* Credits Card */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-primary" />
                <span>{isRTL ? "رصيدك المتبقي" : "Remaining Credits"}</span>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant={remainingCredits > 0 ? "default" : "destructive"} className="text-lg px-3">
                  {remainingCredits}
                </Badge>
                {remainingCredits === 0 && (
                  <Button size="sm" onClick={() => navigate("/packages")}>
                    {isRTL ? "شراء رصيد" : "Buy Credits"}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {remainingCredits === 0 && (
          <Alert className="mb-6" variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {isRTL 
                ? "ليس لديك رصيد كافٍ. يرجى شراء باقة للمتابعة."
                : "You don't have enough credits. Please purchase a package to continue."}
            </AlertDescription>
          </Alert>
        )}

        {/* Submission Form */}
        <Card>
          <CardHeader>
            <CardTitle>{isRTL ? "نوع المحتوى" : "Content Type"}</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3 mb-6">
                <TabsTrigger value="event">
                  {isRTL ? "فعالية" : "Event"}
                </TabsTrigger>
                <TabsTrigger value="x_space">
                  {isRTL ? "مساحة X" : "X Space"}
                </TabsTrigger>
                <TabsTrigger value="social_account">
                  {isRTL ? "حساب تواصل" : "Social Account"}
                </TabsTrigger>
              </TabsList>

              {/* Event Form */}
              <TabsContent value="event" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{isRTL ? "العنوان (إنجليزي)" : "Title (English)"} *</Label>
                    <Input 
                      value={formData.title}
                      onChange={(e) => handleInputChange("title", e.target.value)}
                      placeholder={isRTL ? "عنوان الفعالية" : "Event title"}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{isRTL ? "العنوان (عربي)" : "Title (Arabic)"}</Label>
                    <Input 
                      value={formData.title_ar}
                      onChange={(e) => handleInputChange("title_ar", e.target.value)}
                      placeholder={isRTL ? "عنوان الفعالية بالعربي" : "Event title in Arabic"}
                      dir="rtl"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{isRTL ? "الوصف" : "Description"}</Label>
                  <Textarea 
                    value={formData.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    placeholder={isRTL ? "وصف الفعالية" : "Event description"}
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {isRTL ? "التاريخ" : "Date"}
                    </Label>
                    <Input 
                      type="date"
                      value={formData.date}
                      onChange={(e) => handleInputChange("date", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      {isRTL ? "وقت البدء" : "Start Time"}
                    </Label>
                    <Input 
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => handleInputChange("start_time", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      {isRTL ? "وقت الانتهاء" : "End Time"}
                    </Label>
                    <Input 
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => handleInputChange("end_time", e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      {isRTL ? "الموقع" : "Location"}
                    </Label>
                    <Input 
                      value={formData.location}
                      onChange={(e) => handleInputChange("location", e.target.value)}
                      placeholder={isRTL ? "موقع الفعالية" : "Event location"}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{isRTL ? "التصنيف" : "Category"}</Label>
                    <Select value={formData.category} onValueChange={(v) => handleInputChange("category", v)}>
                      <SelectTrigger>
                        <SelectValue placeholder={isRTL ? "اختر التصنيف" : "Select category"} />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.path}>
                            {isRTL ? cat.name_ar : cat.name_en}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Link2 className="h-4 w-4" />
                    {isRTL ? "رابط خارجي" : "External Link"}
                  </Label>
                  <Input 
                    type="url"
                    value={formData.external_link}
                    onChange={(e) => handleInputChange("external_link", e.target.value)}
                    placeholder="https://..."
                  />
                </div>

                {/* Image Upload Section */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <ImagePlus className="h-4 w-4" />
                    {isRTL ? "صورة الفعالية" : "Event Image"}
                  </Label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageSelect}
                    accept="image/*"
                    className="hidden"
                  />
                  
                  {imagePreview ? (
                    <div className="relative w-full max-w-md">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-48 object-cover rounded-lg border"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-8 w-8"
                        onClick={removeImage}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                    >
                      <ImagePlus className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-muted-foreground">
                        {isRTL ? "اضغط لاختيار صورة" : "Click to select an image"}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {isRTL ? "الحد الأقصى 5MB" : "Max 5MB"}
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* X Space Form */}
              <TabsContent value="x_space" className="space-y-4">
                <div className="space-y-2">
                  <Label>{isRTL ? "عنوان المساحة" : "Space Title"} *</Label>
                  <Input 
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    placeholder={isRTL ? "عنوان المساحة" : "Space title"}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{isRTL ? "الوصف" : "Description"}</Label>
                  <Textarea 
                    value={formData.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    placeholder={isRTL ? "وصف المساحة" : "Space description"}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{isRTL ? "رابط المساحة" : "Space URL"}</Label>
                  <Input 
                    type="url"
                    value={formData.x_space_url}
                    onChange={(e) => handleInputChange("x_space_url", e.target.value)}
                    placeholder="https://x.com/i/spaces/..."
                  />
                </div>

                <div className="space-y-2">
                  <Label>{isRTL ? "موعد البث" : "Scheduled Time"}</Label>
                  <Input 
                    type="datetime-local"
                    value={formData.scheduled_at}
                    onChange={(e) => handleInputChange("scheduled_at", e.target.value)}
                  />
                </div>
              </TabsContent>

              {/* Social Account Form */}
              <TabsContent value="social_account" className="space-y-4">
                <div className="space-y-2">
                  <Label>{isRTL ? "اسم المجموعة/الحساب" : "Group/Account Name"} *</Label>
                  <Input 
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    placeholder={isRTL ? "اسم المجموعة" : "Group name"}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{isRTL ? "الوصف" : "Description"}</Label>
                  <Textarea 
                    value={formData.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    placeholder={isRTL ? "وصف المجموعة" : "Group description"}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{isRTL ? "نوع المنصة" : "Platform Type"}</Label>
                  <Select value={formData.social_type} onValueChange={(v) => handleInputChange("social_type", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder={isRTL ? "اختر المنصة" : "Select platform"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="telegram">Telegram</SelectItem>
                      <SelectItem value="discord">Discord</SelectItem>
                      <SelectItem value="twitter">X / Twitter</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{isRTL ? "رابط المجموعة" : "Group Link"}</Label>
                  <Input 
                    type="url"
                    value={formData.social_link}
                    onChange={(e) => handleInputChange("social_link", e.target.value)}
                    placeholder="https://..."
                  />
                </div>

                <div className="space-y-2">
                  <Label>{isRTL ? "عدد الأعضاء" : "Members Count"}</Label>
                  <Input 
                    type="number"
                    value={formData.members_count}
                    onChange={(e) => handleInputChange("members_count", e.target.value)}
                    placeholder="0"
                  />
                </div>
              </TabsContent>
            </Tabs>

            <Button 
              className="w-full mt-6" 
              size="lg"
              onClick={handleSubmit}
              disabled={submitting || uploadingImage || remainingCredits <= 0}
            >
              {(submitting || uploadingImage) && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {uploadingImage 
                ? (isRTL ? "جاري رفع الصورة..." : "Uploading image...")
                : (isRTL ? "إرسال الطلب" : "Submit Request")
              }
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Submit;
